//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, {
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import DetailsSheet from '@components/molecules/details-sheet/DetailsSheet';
import { InstagramMentionIFramePopover } from '@components/molecules/instagram-mention-iframe/InstagramMentionIFramePopover';
import FacebookTextSegment from '@facebook/components/shared/FacebookTextSegment';
import { useResizeObserver } from '@hooks/useResizeObserver';
import ContentSourceType from '@models/ContentSourceType';
import { SocialNetwork } from '@models/SocialNetwork';
import { isDefined } from '@utils/filterPredicates';
import { parseFacebookText } from '@utils/social-text-utils/facebookTextUtils';
import { parseTwitterText } from '@utils/social-text-utils/twitterTextUtils';
import { ellipsize, parseSocialText } from '@utils/stringUtils';

import {
  InstagramWebLinks,
  TikTokWebLinks,
  TwitterWebLinks,
  YouTubeWebLinks,
} from 'app/constants/social-links';

const INSTAGRAM_HASHTAG_PATH = '/instagram/hashtags/';

interface BaseCaptionProps extends HTMLAttributes<HTMLDivElement> {
  caption: string;
  maxPreviewLength?: number;
  maxLineCount?: number;
}

/**
 * customTextDecorator - Override text decoration
 * shouldDecorateLinks - If true, decorates caption text based on social network
 * socialNetwork - determines the destination of the decorated links
 */
type LinkDecorationTypes =
  | {
      customTextDecorator?: (text: string) => ReactNode;
      shouldDecorateLinks: true;
      socialNetwork: SocialNetwork | undefined;
    }
  | {
      customTextDecorator?: never;
      shouldDecorateLinks?: false;
      socialNetwork?: never;
    };

export type CaptionProps = BaseCaptionProps & LinkDecorationTypes;

/**
 *
 * The Caption atom displays a social post's caption, and
 * will optionally cut the string short. If the caption
 * is longer than the allotted number of characters or line count,
 * hovering the truncated caption will trigger a a popover with
 * the full caption.
 */
export const Caption = ({
  caption,
  className,
  customTextDecorator,
  maxLineCount = Infinity,
  maxPreviewLength = Infinity,
  shouldDecorateLinks,
  socialNetwork,
}: CaptionProps) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const { height, width } = useResizeObserver(containerRef);
  const [hasMaxLineCountReached, setHasMaxLineCountReached] =
    useState<boolean>(false);

  /**
   * On container size change check if the element's scroll height
   * is greater than it's client height (maxLineCount reaches it's
   * limit when it is truthy)
   */
  useEffect(() => {
    if (containerRef.current) {
      setHasMaxLineCountReached(
        containerRef.current.scrollHeight > containerRef.current.clientHeight,
      );
    }
  }, [width, height, caption]);

  const captionPreview = maxPreviewLength
    ? ellipsize(caption, {
        maxLength: maxPreviewLength,
        breakAtBlankSpace: true,
        noSpaceBeforeEllipsis: true,
      })
    : caption;

  const captionPreviewWithLinks = (
    <ParsedTextContainer ref={containerRef} maxLineCount={maxLineCount}>
      {shouldDecorateLinks
        ? decorateText(captionPreview, socialNetwork, customTextDecorator)
        : captionPreview}
    </ParsedTextContainer>
  );

  const captionWithLinks = shouldDecorateLinks
    ? decorateText(caption, socialNetwork, customTextDecorator)
    : caption;

  const isCaptionTruncatedByMaxPreviewLength =
    caption.length > maxPreviewLength;
  const isCaptionTruncatedByMaxLineCount = hasMaxLineCountReached;
  const isCaptionTruncated =
    isCaptionTruncatedByMaxPreviewLength || isCaptionTruncatedByMaxLineCount;

  return (
    <Preview className={className}>
      <DetailsSheet
        appendTarget='body'
        delay={{ showDelay: 300 }}
        isDisabled={!isCaptionTruncated}
        sheetContent={<div>{captionWithLinks}</div>}
      >
        {captionPreviewWithLinks}
      </DetailsSheet>
    </Preview>
  );
};

const decorateText = (
  text: string,
  socialNetwork: SocialNetwork | undefined,
  customTextDecorator?: (text: string) => ReactNode,
) => {
  if (isDefined(customTextDecorator)) {
    return (
      <ParsedTextContainer>{customTextDecorator(text)}</ParsedTextContainer>
    );
  }

  if (!isDefined(socialNetwork)) {
    return text;
  }

  switch (socialNetwork) {
    case SocialNetwork.Instagram:
      return decorateInstagramText(text);

    case SocialNetwork.TikTok:
      return decorateTikTokText(text);

    case SocialNetwork.Twitter:
      return decorateTwitterText(text);

    case SocialNetwork.Pinterest:
      return decoratePinterestText(text);

    case SocialNetwork.Facebook:
      return decorateFacebookText(text);

    case SocialNetwork.YouTube:
      return decorateYouTubeText(text);

    // TODO: PD-196923 - Consider adding link decoration logic for the other networks
    case SocialNetwork.Snapchat:
    case SocialNetwork.Tumblr:
    case SocialNetwork.Unknown:
      return text;

    default: {
      const exhaustiveMatchingGuard = (_: never) => {
        console.error(
          'Impossible case! This is only reachable if a case was missed.',
        );
      };
      exhaustiveMatchingGuard(socialNetwork);

      return text;
    }
  }
};

export interface CaptionLinkProps {
  href: string;
  text: string;
}

const CaptionLink = ({ href, text }: CaptionLinkProps) => {
  return (
    <StyledPrimaryAnchor href={href} target='_blank'>
      {text}
    </StyledPrimaryAnchor>
  );
};

Caption.CaptionLink = CaptionLink;

const decorateInstagramText = (str: string) => {
  const parsedText = parseSocialText(str);
  const parsedSocialTextComponent = parsedText.map((item, index) => {
    if (item.type === ContentSourceType.HASHTAG) {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={`${INSTAGRAM_HASHTAG_PATH}${item.value.replace('#', '')}`}
          text={item.value}
        />
      );
    }

    if (item.type === ContentSourceType.MENTION && item.value.length > 1) {
      const username = item.value.replace('@', '');

      return (
        <InstagramMentionIFramePopover
          key={`${item.value}-${index}`}
          username={username}
        >
          <CaptionLink
            href={InstagramWebLinks.getUserLink(username)}
            text={item.value}
          />
        </InstagramMentionIFramePopover>
      );
    }

    if (item.type === ContentSourceType.LINK) {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={item.value}
          text={item.value}
        />
      );
    } else {
      return <span key={`${item.value}-${index}`}>{item.value}</span>;
    }
  });

  return <ParsedTextContainer>{parsedSocialTextComponent}</ParsedTextContainer>;
};

const decorateTwitterText = (str: string) => {
  const parsedText = parseSocialText(str);
  const parsedSocialTextComponent = parsedText.map((item, index) => {
    if (item.type === ContentSourceType.HASHTAG) {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={TwitterWebLinks.getHashtagLink(item.value.replace('#', ''))}
          text={item.value}
        />
      );
    } else if (item.type === ContentSourceType.MENTION) {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={TwitterWebLinks.getUserLink(item.value)}
          text={item.value}
        />
      );
    } else {
      return <span key={`${item.value}-${index}`}>{item.value}</span>;
    }
  });

  return <ParsedTextContainer>{parsedSocialTextComponent}</ParsedTextContainer>;
};

const decoratePinterestText = (str: string) => {
  const parsedText = parseTwitterText(str);
  const parsedSocialTextComponent = parsedText.map((item, index) => {
    if (item.type === 'Url') {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={item.value}
          text={item.value}
        />
      );
    } else {
      return <span key={`${item.value}-${index}`}>{item.value}</span>;
    }
  });

  return <ParsedTextContainer>{parsedSocialTextComponent}</ParsedTextContainer>;
};

const decorateFacebookText = (str: string) => {
  const parsedText = parseFacebookText(str);
  return (
    <ParsedTextContainer>
      {parsedText.map((entity, index) => (
        <FacebookTextSegment
          key={`${entity.value}-${index}`}
          entity={entity}
          isWeekView={false}
        />
      ))}
    </ParsedTextContainer>
  );
};

const decorateTikTokText = (str: string) => {
  const parsedText = parseSocialText(str);

  const parsedSocialTextComponent = parsedText.map((item, index) => {
    if (item.type === ContentSourceType.HASHTAG) {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={TikTokWebLinks.getHashtagLink(item.value.replace('#', ''))}
          text={item.value}
        />
      );
    } else if (item.type === ContentSourceType.MENTION) {
      return (
        <CaptionLink
          key={`${item.value}-${index}`}
          href={TikTokWebLinks.getUserLink(item.value.substring(1))}
          text={item.value}
        />
      );
    } else {
      return <span key={`${item.value}-${index}`}>{item.value}</span>;
    }
  });

  return <ParsedTextContainer>{parsedSocialTextComponent}</ParsedTextContainer>;
};

const decorateYouTubeText = (str: string) => {
  const parsedText = parseSocialText(str);

  const parsedSocialTextComponent = parsedText.map((item, index) => {
    switch (item.type) {
      case ContentSourceType.HASHTAG:
        return (
          <CaptionLink
            key={`${item.value}-${index}`}
            href={YouTubeWebLinks.getHashtagLink(item.value.replace('#', ''))}
            text={item.value}
          />
        );
      case ContentSourceType.LINK:
        return (
          <CaptionLink
            key={`${item.value}-${index}`}
            href={item.value}
            text={item.value}
          />
        );
      case ContentSourceType.MENTION:
        return (
          <CaptionLink
            key={`${item.value}-${index}`}
            href={YouTubeWebLinks.getUserLink(item.value.substring(1))}
            text={item.value}
          />
        );
      default:
        return <span key={`${item.value}-${index}`}>{item.value}</span>;
    }
  });

  return <ParsedTextContainer>{parsedSocialTextComponent}</ParsedTextContainer>;
};

const Preview = styled.div`
  display: inline-block;
  max-width: 100%;
`;

const ParsedTextContainer = styled.span<{ maxLineCount?: number }>(
  ({ maxLineCount }) => css`
    white-space: pre-line;
    ${maxLineCount &&
    `
    -webkit-line-clamp: ${maxLineCount};
    -webkit-box-orient: vertical;
    display: -webkit-box;
    overflow: hidden;
  `};
  `,
);

const StyledPrimaryAnchor = styled(PrimaryAnchor)`
  word-break: break-word;
`;
